import { useEffect, useState } from 'react';
import Button from '@/components/common/Button';
import FaIcon from '@/components/common/FaIcon';
import { getErrorMessage, parseApiError } from '@/utils/api-errors';
import {
  browserSupportsPasskeys,
  fetchPasskeyAvailability,
  PasskeyCancelledError,
  signInWithPasskey,
  type PasskeySignInResult,
} from '@/services/passkeys';

interface Props {
  /** Same completion path as a password sign-in (stores token + user, navigates). */
  onSignedIn: (result: PasskeySignInResult) => void;
  /** Disable while another sign-in is in flight. */
  disabled?: boolean;
}

/**
 * "Sign in with a passkey" (P4-07). Rendered only when this browser supports
 * WebAuthn AND the server reports passkeys available, so a deployment without
 * the WebAuthn library never shows a button that cannot work. A passkey with
 * user verification satisfies the MFA policy: there is no code prompt after it.
 */
export default function PasskeySignInButton({ onSignedIn, disabled }: Props) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!browserSupportsPasskeys()) return;
    let cancelled = false;
    void fetchPasskeyAvailability().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!available) return null;

  async function start() {
    setError(null);
    setBusy(true);
    try {
      const result = await signInWithPasskey();
      onSignedIn(result);
    } catch (err) {
      if (!(err instanceof PasskeyCancelledError)) {
        setError(getErrorMessage(parseApiError(err)) || 'That passkey could not be verified. Try again or use another sign-in method.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 space-y-2">
      <Button
        type="button"
        variant="outline"
        fullWidth
        onClick={start}
        disabled={disabled || busy}
        icon={<FaIcon icon="fingerprint" className="h-4 w-4" />}
        className="text-gray-200 border-white/15 bg-transparent dark:bg-transparent shadow-none hover:bg-white/5"
      >
        {busy ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
      </Button>
      {error && (
        <div role="alert" className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </div>
  );
}
